import { useCallback, useMemo } from 'react'

import { useUploadProgress } from '../useUploadProgress'
import { useProgressAnimation } from './useProgressAnimation'
import { useUploadState } from './useUploadState'
import { useWidgetHandlers } from './useWidgetHandlers'

export const useUploadProgressWidget = () => {
  const { uploads, activeUploads, cancelUpload, removeUpload, allSuccess, hasErrors, addFiles, allCompleted } =
    useUploadProgress()

  const uploadsArray = useMemo(() => uploads, [uploads])

  const { displayedProgress, instantReset } = useProgressAnimation({
    activeUploads,
    allSuccess,
    allCompleted
  })

  const { expanded, isFadingOut, setExpanded } = useUploadState({
    activeUploads,
    allCompleted
  })

  const { handleCancelAll, handleClose, handleAddFiles } = useWidgetHandlers({
    activeUploads,
    uploadsArray,
    cancelUpload,
    removeUpload,
    addFiles
  })

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev)
  }, [setExpanded])

  const activeCount = activeUploads.length
  const nonCancelledUploads = useMemo(() => uploadsArray.filter((u) => u.status !== 'cancelled'), [uploadsArray])
  const cancelledUploads = useMemo(() => uploadsArray.filter((u) => u.status === 'cancelled'), [uploadsArray])
  const hasSuccessfulUploads = useMemo(() => uploadsArray.some((u) => u.status === 'success'), [uploadsArray])

  const showCancelledState = useMemo(
    () => cancelledUploads.length > 0 && !hasSuccessfulUploads && activeCount === 0,
    [cancelledUploads.length, hasSuccessfulUploads, activeCount]
  )

  const totalCount = nonCancelledUploads.length
  const hasErrorsOnly = hasErrors && activeCount === 0 && nonCancelledUploads.length > 0 && !allSuccess
  const showSuccessState = allSuccess && activeCount === 0 && nonCancelledUploads.length > 0

  return {
    uploadsArray,
    expanded,
    displayedProgress,
    activeCount,
    totalCount,
    hasErrorsOnly,
    showSuccessState,
    showCancelledState,
    isFadingOut,
    instantReset,
    handleToggle,
    handleCancelAll,
    handleClose,
    handleAddFiles
  }
}
